/**
 * A one-shot "pending jump" the link bar hands to the scroller across a
 * cross-tab navigation.
 *
 * A section-anchor shortcut's `<a href>` keeps its shareable `#…` fragment (for
 * copy-link / crawlers / no-JS), but the CLICK navigates via a CLEAN
 * `router.push` (the fragment-free tab route). That's deliberate: Next 16.2.x
 * stores a fragment in its own router state when you navigate to a `#…` URL and
 * restores it on the next same-route `<Link>` nav — so a plain "My Store" tab
 * click would resurrect the stale `#collections/…`. Navigating clean keeps the
 * router state fragment-free; the scroll target (and its deep-page fallback for
 * when it sits on an unloaded infinite-scroll page) rides here instead, consumed
 * by `useBioHashScroll` when the destination tab mounts, which then reflects the
 * shareable fragment via the History API. Module state survives soft navigation.
 *
 * One-shot: {@link takePendingLinkJump} clears it on read, so only the click that
 * set it triggers the jump — never a later back/forward or tab re-selection.
 */
export interface PendingLinkJump {
  /** The `data-bio-link` anchor path to scroll to (`collections/<id>`, `series/<id>`). */
  anchorPath: string;
  /** Deep page to navigate to if the anchor isn't on the loaded tab page. */
  fallbackHref?: string;
}

let pending: PendingLinkJump | undefined;

export function setPendingLinkJump(jump: PendingLinkJump): void {
  pending = jump;
}

export function takePendingLinkJump(): PendingLinkJump | undefined {
  const jump = pending;
  pending = undefined;
  return jump;
}
